import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Breadcrumb,
  Card,
  Col,
  Container,
  Row,
} from "react-bootstrap";

import { Button } from "primereact/button";
import { Chip } from "primereact/chip";
import {
  ConfirmDialog,
  confirmDialog,
} from "primereact/confirmdialog";
import { Skeleton } from "primereact/skeleton";

import {
  PiCoinsDuotone,
  PiCrownSimpleDuotone,
} from "react-icons/pi";

import { IoImagesOutline } from "react-icons/io5";
import { GrDocumentZip } from "react-icons/gr";
import { FiEye } from "react-icons/fi";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { toast } from "react-toastify";

import { useAuth } from "../context/AuthContext";
import api from "../api/axios";
import SEO from "../components/SEO";
import ProtectedCanvasImage from "../components/ProtectedCanvasImage";
import { slugify } from "../utils/slugify";

import difbundle from "../assets/images/difbundle.svg";
import "../assets/css/Shopdetails.css";

// Must match the shop's persisted state (see Shop.js / Productdetails.js).
const SHOP_STATE_KEY = "shop_state_v1";

const SHOP_DEFAULT_STATE = {
  category: { category_name: "", category_id: 0 },
  subcategory: null,
  searchTerm: "",
  sortBy: "name_asc",
  first: 0,
  rows: 21,
  occasionId: null,
};

const Bulkproductdetails = () => {
  const {
    isAuthenticated,
    tierlevel,
  } = useAuth();

  const { bundleId } = useParams();
  const navigate = useNavigate();

  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);

  const [
    downloadLoading,
    setDownloadLoading,
  ] = useState(false);

  const [images, setImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const [
    relatedBundles,
    setRelatedBundles,
  ] = useState([]);

  const [
    relatedLoading,
    setRelatedLoading,
  ] = useState(false);

  const [recommendationSections, setRecommendationSections] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);

  const cache = useRef(new Map());
  const inFlight = useRef(new Map());

  const tierNames = {
    1: "Starter",
    2: "Premium",
    3: "Premium +",
    4: "Pro",
  };

  const userTier = Number(tierlevel || 0);

  const minRequiredTier = Number(
    bundle?.min_subscription || 0
  );

  const hasCreditDiscount =
    userTier === 3 ||
    userTier === 4;

  const getDiscountPercentage = (
    tierLevel
  ) => {
    switch (Number(tierLevel)) {
      case 3:
        return 10;

      case 4:
        return 15;

      default:
        return 0;
    }
  };

  const getDiscountedCreditCost = (
    baseCreditCost,
    tierLevel
  ) => {
    const cost = Number(
      baseCreditCost ?? 0
    );

    switch (Number(tierLevel)) {
      case 3:
        return Math.round(
          cost * 0.9
        );

      case 4:
        return Math.round(
          cost * 0.85
        );

      default:
        return Math.round(cost);
    }
  };

  const canDownload =
    isAuthenticated &&
    (
      !minRequiredTier ||
      userTier >= minRequiredTier
    );

  const needsPurchaseSubscription =
    isAuthenticated &&
    userTier === 0 &&
    minRequiredTier > 0;

  const needsUpgrade =
    isAuthenticated &&
    userTier > 0 &&
    minRequiredTier > 0 &&
    userTier < minRequiredTier;

  const selectedImage =
    images[selectedImageIndex] || images[0] || null;

  const categoryName =
    bundle?.category?.category_name || "";

  const saveShopState = (nextState) => {
    try {
      localStorage.setItem(SHOP_STATE_KEY, JSON.stringify(nextState));
    } catch {
      // storage unavailable — navigation state still carries the filters
    }

    window.dispatchEvent(
      new CustomEvent("shop-state-updated", { detail: nextState })
    );
  };

  const goToShopReset = () => {
    saveShopState({ ...SHOP_DEFAULT_STATE });

    navigate("/shop", {
      state: {
        reset: true,
        category: "",
        subcategory: null,
        searchTerm: "",
        occasionId: null,
        sortBy: "name_asc",
      },
    });
  };

  const goToCategory = () => {
    if (!categoryName) return;

    saveShopState({
      ...SHOP_DEFAULT_STATE,
      category: { category_name: categoryName },
      subcategory: null,
    });

    navigate("/shop", {
      state: {
        category: categoryName,
        subcategory: null,
        searchTerm: "",
        reset: false,
      },
    });
  };

  const prepareImages = (data) => {
    if (!Array.isArray(data?.images)) {
      setImages([]);
      return;
    }

    const formatted = [
      ...data.images,
    ]
      .sort(
        (a, b) =>
          Number(a.order_no || 0) -
          Number(b.order_no || 0)
      )
      .map((img, index) => ({
        itemImageSrc:
          img.image_url,

        thumbnailImageSrc:
          img.image_url,

        alt:
          `${data.bundle_name || "Bundle"} image ${index + 1}`,
      }));

    setImages(formatted);
    setSelectedImageIndex(0);
  };

  useEffect(() => {
    if (!bundleId) {
      return;
    }

    const fetchBundle = async () => {
      const cacheKey =
        String(bundleId);

      if (
        cache.current.has(cacheKey)
      ) {
        const cached =
          cache.current.get(
            cacheKey
          );

        setBundle(cached);
        prepareImages(cached);
        setLoading(false);

        return;
      }

      if (
        inFlight.current.has(
          cacheKey
        )
      ) {
        return;
      }

      setLoading(true);

      const request = api
        .get(
          `/pbundles/customer/singlebundle/${bundleId}`
        )
        .then((res) => {
          if (res.data?.success) {
            const data =
              res.data.data;

            cache.current.set(
              cacheKey,
              data
            );

            setBundle(data);
            prepareImages(data);

            return;
          }

          toast.error(
            res.data?.message ||
            "Bundle not found"
          );
        })
        .catch((error) => {
          console.error(
            "Bundle fetch failed:",
            error
          );

          toast.error(
            error?.response?.data
              ?.message ||
            "Failed to fetch bundle"
          );
        })
        .finally(() => {
          setLoading(false);

          inFlight.current.delete(
            cacheKey
          );
        });

      inFlight.current.set(
        cacheKey,
        request
      );
    };

    fetchBundle();
  }, [bundleId]);

  useEffect(() => {
    if (!bundleId) {
      return;
    }

    const fetchRelatedBundles =
      async () => {
        try {
          setRelatedLoading(true);

          const res =
            await api.get(
              `/pbundles/customer/related-bundles/${bundleId}`,
              {
                params: {
                  limit: 4,
                },
              }
            );

          if (res.data?.success) {
            setRelatedBundles(
              res.data.data || []
            );
          }
        } catch (error) {
          console.error(
            "Related bundles fetch failed:",
            error
          );

          toast.error(
            error?.response?.data
              ?.message ||
            "Failed to fetch related bundles"
          );
        } finally {
          setRelatedLoading(false);
        }
      };

    fetchRelatedBundles();
  }, [bundleId]);

  useEffect(() => {
    if (!bundleId) {
      return;
    }

    let cancelled = false;

    const fetchRecommendations = async () => {
      setRecommendationSections([]);
      setRecommendationsLoading(true);

      try {
        const res = await api.get(
          `/ProductRoutes/customer/bundle-recommendations/${bundleId}`
        );

        if (cancelled) return;

        setRecommendationSections(
          res.data?.success && Array.isArray(res.data.sections)
            ? res.data.sections
            : []
        );
      } catch (err) {
        if (!cancelled) {
          console.warn("bundle recommendations fetch failed:", err);
          setRecommendationSections([]);
        }
      } finally {
        if (!cancelled) {
          setRecommendationsLoading(false);
        }
      }
    };

    fetchRecommendations();

    return () => {
      cancelled = true;
    };
  }, [bundleId]);

  const parseBlobResponse =
    async (blobData) => {
      if (!(blobData instanceof Blob)) {
        return blobData || {};
      }

      try {
        const text =
          await blobData.text();

        if (!text) {
          return {};
        }

        return JSON.parse(text);
      } catch (error) {
        console.error(
          "Could not parse bundle response:",
          error
        );

        return {};
      }
    };

  const getDownloadFilename = (
    contentDisposition
  ) => {
    const fallbackName =
      `${bundle?.bundle_name || "bundle"}.zip`;

    if (!contentDisposition) {
      return fallbackName;
    }

    const utf8Match =
      contentDisposition.match(
        /filename\*=UTF-8''([^;]+)/i
      );

    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(
          utf8Match[1]
        );
      } catch {
        return utf8Match[1];
      }
    }

    const filenameMatch =
      contentDisposition.match(
        /filename="?([^";]+)"?/i
      );

    return (
      filenameMatch?.[1] ||
      fallbackName
    );
  };

  const saveBundleFile = (
    blobData,
    responseHeaders
  ) => {
    const contentType =
      responseHeaders[
        "content-type"
      ] || "application/zip";

    const blob = new Blob(
      [blobData],
      {
        type: contentType,
      }
    );

    const fileName =
      getDownloadFilename(
        responseHeaders[
          "content-disposition"
        ]
      );

    const downloadUrl =
      window.URL.createObjectURL(
        blob
      );

    const link =
      document.createElement("a");

    link.href = downloadUrl;
    link.download = fileName;

    document.body.appendChild(
      link
    );

    link.click();
    link.remove();

    window.URL.revokeObjectURL(
      downloadUrl
    );
  };

  const handleBundleDownload =
    async (
      confirmRedownload = false
    ) => {
      if (downloadLoading) {
        return;
      }

      if (!bundleId) {
        toast.warn(
          "Bundle information is missing."
        );

        return;
      }

      setDownloadLoading(true);

      try {
        const response =
          await api.post(
            `/cpdroutes/download-bundle/${bundleId}`,
            {
              confirm_redownload:
                confirmRedownload,
            },
            {
              responseType: "blob",
            }
          );

        const downloadStatus =
          response.headers[
            "x-download-status"
          ];

        const contentType =
          String(
            response.headers[
              "content-type"
            ] || ""
          ).toLowerCase();

        const blobLooksLikeFile =
          response.status === 200 &&
          response.data instanceof
            Blob &&
          response.data.size > 0 &&
          (
            contentType.includes(
              "zip"
            ) ||
            contentType.includes(
              "application/octet-stream"
            )
          );

        if (
          downloadStatus !==
            "success" &&
          !blobLooksLikeFile
        ) {
          throw new Error(
            "The server did not return a valid bundle ZIP."
          );
        }

        saveBundleFile(
          response.data,
          response.headers
        );

        const isRedownload =
          response.headers[
            "x-redownload"
          ] === "true";

        toast.success(
          isRedownload
            ? "Bundle downloaded again successfully."
            : "Bundle download started."
        );

        window.dispatchEvent(
          new Event(
            "wallet-updated"
          )
        );
      } catch (error) {
        console.error(
          "Bundle download failed:",
          error
        );

        const status =
          error?.response?.status;

        const errorData =
          await parseBlobResponse(
            error?.response?.data
          );

        if (
          status === 409 &&
          (
            errorData
              ?.already_downloaded ||
            errorData
              ?.confirmation_required
          )
        ) {
          const pricing =
            errorData?.pricing ||
            {};

          const previousDownload =
            errorData
              ?.previous_download ||
            {};

          const baseCreditCost =
            Number(
              pricing
                .base_credit_cost ??
              bundle?.credit_cost ??
              0
            );

          const payableCreditCost =
            Number(
              pricing
                .payable_credit_cost ??
              getDiscountedCreditCost(
                bundle?.credit_cost,
                userTier
              )
            );

          const discountPercentage =
            Number(
              pricing
                .discount_percentage ??
              getDiscountPercentage(
                userTier
              )
            );

          const previousDate =
            previousDownload
              ?.downloaded_at
              ? new Date(
                  previousDownload
                    .downloaded_at
                ).toLocaleString(
                  "en-IN",
                  {
                    dateStyle:
                      "medium",

                    timeStyle:
                      "short",
                  }
                )
              : null;

          confirmDialog({
            header:
              "Bundle already downloaded",

            icon:
              "pi pi-exclamation-triangle",

            message: (
              <div
                className="d-flex flex-column gap-3"
                style={{
                  maxWidth:
                    "440px",
                }}
              >
                <div>
                  You have already
                  downloaded this bundle.
                  Downloading it again
                  will deduct credits
                  again.
                </div>

                {previousDate && (
                  <div
                    className="text-secondary"
                    style={{
                      fontSize:
                        "14px",
                    }}
                  >
                    Previously downloaded
                    on{" "}
                    <strong>
                      {previousDate}
                    </strong>
                  </div>
                )}

                <div
                  className="p-3 rounded"
                  style={{
                    background:
                      "rgba(255, 113, 188, 0.08)",

                    border:
                      "1px solid rgba(255, 113, 188, 0.2)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center gap-3">
                    <span>
                      Credits to be
                      deducted
                    </span>

                    <span className="text-pink d-flex align-items-center gap-1">
                      <PiCoinsDuotone />

                      <strong>
                        {
                          payableCreditCost
                        }
                      </strong>

                      {discountPercentage >
                        0 &&
                        baseCreditCost !==
                          payableCreditCost && (
                          <del
                            className="text-secondary"
                            style={{
                              fontSize:
                                "12px",
                            }}
                          >
                            {
                              baseCreditCost
                            }
                          </del>
                        )}
                    </span>
                  </div>

                  {discountPercentage >
                    0 && (
                    <small className="text-secondary">
                      Your{" "}
                      {
                        discountPercentage
                      }
                      % subscription
                      discount has been
                      applied.
                    </small>
                  )}
                </div>

                <small className="text-secondary">
                  No credits will be
                  deducted unless you
                  confirm the
                  re-download.
                </small>
              </div>
            ),

            acceptLabel:
              `Download Again (${payableCreditCost} coins)`,

            rejectLabel:
              "Cancel",

            acceptIcon:
              "pi pi-download",

            rejectIcon:
              "pi pi-times",

            acceptClassName:
              "button-pink",

            rejectClassName:
              "p-button-text p-button-secondary",

            closable: true,
            draggable: false,

            accept: () => {
              handleBundleDownload(
                true
              );
            },

            reject: () => {
              toast.info(
                "Bundle re-download cancelled."
              );
            },
          });

          return;
        }

        if (
          status === 403 ||
          errorData
            ?.upgrade_required
        ) {
          toast.warn(
            errorData?.message ||
            "Please upgrade your subscription to download this bundle."
          );

          return;
        }

        if (status === 400) {
          toast.warn(
            errorData?.message ||
            "Insufficient wallet balance."
          );

          return;
        }

        if (status === 401) {
          toast.error(
            "Please log in again to continue."
          );

          return;
        }

        toast.error(
          errorData?.message ||
          error?.message ||
          "Failed to download bundle."
        );
      } finally {
        setDownloadLoading(false);
      }
    };

  const renderCreditPrice = (
    creditCost
  ) => {
    const originalCost =
      Math.round(
        Number(creditCost ?? 0)
      );

    const discountedCost =
      getDiscountedCreditCost(
        creditCost,
        userTier
      );

    const shouldShowDiscount =
      hasCreditDiscount &&
      originalCost !==
        discountedCost;

    if (shouldShowDiscount) {
      return (
        <span className="d-flex align-items-center gap-1">
          <b>{discountedCost}</b>

          <del
            style={{
              fontSize: "12px",
              opacity: 0.65,
            }}
          >
            {originalCost}
          </del>
        </span>
      );
    }

    return (
      <b>
        {originalCost || "Free"}
      </b>
    );
  };

  /* Same card + slider as the product details recommendations */
  const renderProductCard = (p) => {
    const productUrl = `/product/${p.product_id}/${slugify(p.product_name)}`;

    return (
      <SwiperSlide key={p.product_id}>
        <Card
          className="card-click"
          style={{ cursor: "pointer" }}
          onContextMenu={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onDragStart={(event) => {
            event.preventDefault();
          }}
        >
          <ProtectedCanvasImage
            src={p.thumbnail?.image_url || "/placeholder.png"}
            alt={p.product_name}
            height={160}
            objectFit="cover"
            objectPosition="top center"
            onClick={() => navigate(productUrl)}
          />

          {p.is_featured && (
            <div className="prime_icon">
              <PiCrownSimpleDuotone />
            </div>
          )}

          <div
            className="imageoption"
            onClick={() => navigate(productUrl)}
          >
            <div className="buttonlist">
              <div
                className="view"
                title="View"
                onClick={() => navigate(productUrl)}
              >
                <FiEye />
              </div>
            </div>
          </div>

          <div className="product-title">
            <p onClick={() => navigate(productUrl)}>
              {p.product_name}
            </p>

            <span
              className="text-pink d-flex align-items-center gap-1"
              style={{ fontSize: "16px" }}
            >
              <PiCoinsDuotone /> {renderCreditPrice(p.credit_cost)}
            </span>
          </div>
        </Card>
      </SwiperSlide>
    );
  };

  const renderBundleCard = (b) => {
    const bundleUrl = `/bundleproductdetails/${b.product_bundle_id}`;

    const firstImage =
      [...(b.images || [])].sort(
        (x, y) => Number(x.order_no || 0) - Number(y.order_no || 0)
      )[0]?.image_url || difbundle;

    return (
      <SwiperSlide key={b.product_bundle_id}>
        <Card
          className="card-click"
          style={{ cursor: "pointer" }}
          onContextMenu={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onDragStart={(event) => {
            event.preventDefault();
          }}
        >
          <ProtectedCanvasImage
            src={firstImage}
            alt={b.bundle_name}
            height={160}
            objectFit="cover"
            objectPosition="top center"
            onClick={() => navigate(bundleUrl)}
          />

          {b.is_featured && (
            <div className="prime_icon">
              <PiCrownSimpleDuotone />
            </div>
          )}

          <div className="imageoption" onClick={() => navigate(bundleUrl)}>
            <div className="buttonlist">
              <div className="view" title="View" onClick={() => navigate(bundleUrl)}>
                <FiEye />
              </div>
            </div>
          </div>

          <div className="product-title">
            <p onClick={() => navigate(bundleUrl)}>
              {b.bundle_name}
            </p>

            <span
              className="text-pink d-flex align-items-center gap-1"
              style={{ fontSize: "16px" }}
            >
              <PiCoinsDuotone /> {renderCreditPrice(b.credit_cost)}
            </span>
          </div>
        </Card>
      </SwiperSlide>
    );
  };

  const renderProductSlider = (
    title,
    items = [],
    loading = false,
    renderCard = renderProductCard
  ) => {
    if (!loading && (!Array.isArray(items) || items.length === 0)) {
      return null;
    }

    return (
      <Row
        className="mx-0 mt-lg-5 mt-4 justify-content-between"
        style={{ overflow: "hidden" }}
      >
        <h3>
          {title}

          {loading && (
            <i
              className="pi pi-spin pi-spinner ms-2"
              style={{ fontSize: "1rem", color: "#aaa" }}
            />
          )}
        </h3>

        <div>
          {loading ? (
            <div className="d-flex gap-3 mt-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={{ minWidth: 160, flex: "0 0 160px" }}>
                  <Skeleton width="160px" height="160px" className="dark-skeleton mb-2" />
                  <Skeleton width="130px" height="0.9rem" className="dark-skeleton mb-1" />
                  <Skeleton width="60px" height="0.9rem" className="dark-skeleton" />
                </div>
              ))}
            </div>
          ) : (
            <Swiper
              spaceBetween={20}
              slidesPerView={6}
              navigation
              modules={[Navigation]}
              className="mt-4"
              breakpoints={{
                0: { slidesPerView: 2 },
                576: { slidesPerView: 3 },
                768: { slidesPerView: 4 },
                992: { slidesPerView: 6 },
              }}
            >
              {items.map(renderCard)}
            </Swiper>
          )}
        </div>
      </Row>
    );
  };

  return (
    <>
      <ConfirmDialog />

      <SEO
        title={
          bundle?.bundle_name
            ? `${bundle.bundle_name} | Bundle`
            : "Bundle Details"
        }
        description={
          bundle?.description ||
          "Browse premium wedding design bundles and downloadable digital assets."
        }
        canonical={`https://shaadimaterial.com/bundleproductdetails/${bundleId}`}
        keywords="wedding bundles, digital assets, wedding illustrations, downloadable resources"
        image={
          images?.[0]
            ?.itemImageSrc ||
          "https://shaadimaterial.com/logo192.png"
        }
      />

      <Container className="py-8">
        <Row className="mx-0 breadcrumb_part">
          <Breadcrumb>
            <Breadcrumb.Item onClick={() => navigate(isAuthenticated ? "/home" : "/")}>
              Home
            </Breadcrumb.Item>

            <Breadcrumb.Item onClick={goToShopReset}>Shop</Breadcrumb.Item>

            {categoryName && (
              <Breadcrumb.Item onClick={goToCategory}>
                {categoryName}
              </Breadcrumb.Item>
            )}

            <Breadcrumb.Item active className="elipSEC">
              {bundle?.bundle_name || "Details"}
            </Breadcrumb.Item>
          </Breadcrumb>
        </Row>

        <Row className="mx-0 pt-4 pt-lg-5 justify-content-start">
          <Col md={12} lg={4} xs={12} className="product-details">
            <div className="waterihmb_img">
              {loading ? (
                <Skeleton width="100%" height="420px" className="dark-skeleton" />
              ) : selectedImage ? (
                <ProtectedCanvasImage
                  src={selectedImage.itemImageSrc}
                  alt={selectedImage.alt}
                  height="420px"
                  objectFit="contain"
                  objectPosition="top center"
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                />
              ) : (
                <img
                  src={difbundle}
                  alt={bundle?.bundle_name || "Bundle"}
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              )}
            </div>

            {!loading && images.length > 1 && (
              <div className="bundle-thumbs">
                {images.map((img, index) => (
                  <button
                    type="button"
                    key={img.itemImageSrc}
                    className={`bundle-thumb ${index === selectedImageIndex ? "active" : ""}`}
                    onClick={() => setSelectedImageIndex(index)}
                    aria-label={`Show ${img.alt}`}
                  >
                    <ProtectedCanvasImage
                      src={img.thumbnailImageSrc}
                      alt={img.alt}
                      height={64}
                      objectFit="cover"
                      objectPosition="top center"
                    />
                  </button>
                ))}
              </div>
            )}
          </Col>

          <Col md={12} lg={7} xs={12} className="product-details ps-lg-5">
            {!isAuthenticated && (
              <Alert className="mb-4 py-1 fade alert alert-warning show text-start d-flex justify-content-between align-items-center">
                You are not authorized for enterprise commercial use yet
                <Alert.Link
                  as="button"
                  onClick={() => navigate("/packages")}
                  className="ms-3 btn btn-warning text-black"
                  style={{ width: "140px" }}
                >
                  Upgrade Now
                </Alert.Link>
              </Alert>
            )}

            {loading ? (
              <>
                <Skeleton width="60%" height="2rem" className="mb-3 dark-skeleton" />
                <Skeleton width="100%" height="1rem" className="mb-2 dark-skeleton" />
                <Skeleton width="90%" height="1rem" className="mb-4 dark-skeleton" />
                <Skeleton width="100%" height="120px" />
              </>
            ) : (
              <>
                <h2 className="mb-2">{bundle?.bundle_name}</h2>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "20px",
                  }}
                >
                  <IoImagesOutline color="#ff71bc" size={20} />
                  <span style={{ fontSize: "14px", color: "#888" }}>
                    Bundle · {bundle?.count_of_assets || 0} asset
                    {Number(bundle?.count_of_assets) === 1 ? "" : "s"}
                  </span>
                </div>

                <p style={{ width: "90%" }}>{bundle?.description || ""}</p>

                <Row className="mx-0 mt-4">
                  <Col md={2} lg={3} xs={6} className="px-1 mb-2">
                    <Card
                      className="imgTyp img-selected"
                      style={{ position: "relative" }}
                    >
                      {minRequiredTier > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: "-10px",
                            right: "15px",
                            backgroundColor: "#FF9800",
                            color: "#000",
                            borderRadius: "20px",
                            padding: "1px 6px",
                            fontSize: "15px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            zIndex: 1,
                          }}
                          title={`Requires ${tierNames[minRequiredTier] || "higher plan"}`}
                        >
                          <PiCrownSimpleDuotone />
                          <span style={{ fontSize: "10px", fontWeight: "300", color: "#000" }}>
                            {tierNames[minRequiredTier] || "Premium"}
                          </span>
                        </div>
                      )}

                      <i>
                        <GrDocumentZip />
                      </i>

                      <span className="text-pink d-flex justify-content-between align-items-center flex-column">
                        <strong>ZIP</strong>
                        <small className="text-muted">
                          <PiCoinsDuotone />
                          {renderCreditPrice(bundle?.credit_cost)}
                        </small>
                      </span>
                    </Card>
                  </Col>
                </Row>

                <div className="d-flex gap-2">
                  {!isAuthenticated ? (
                    <Button
                      className="my-4 d-block py-2 button-pink"
                      style={{ fontSize: "22px", width: "340px" }}
                      label="Login to Download"
                      onClick={() => navigate("/login")}
                    />
                  ) : needsPurchaseSubscription ? (
                    <Button
                      className="my-4 d-block py-2 button-pink"
                      style={{ fontSize: "22px", width: "340px" }}
                      label="Activate Subscription"
                      onClick={() => navigate("/packages")}
                    />
                  ) : needsUpgrade ? (
                    <Button
                      className="my-4 d-block py-2 button-pink"
                      style={{ fontSize: "22px", width: "340px" }}
                      label={`Upgrade to ${tierNames[minRequiredTier] || "a higher plan"}`}
                      onClick={() => navigate("/packages")}
                    />
                  ) : canDownload ? (
                    <Button
                      className="my-4 d-block py-2 button-pink"
                      style={{ fontSize: "22px", width: "340px" }}
                      disabled={downloadLoading}
                      loading={downloadLoading}
                      onClick={() => handleBundleDownload(false)}
                      label={
                        downloadLoading ? (
                          "Preparing download..."
                        ) : (
                          <span className="d-flex align-items-center justify-content-center gap-2">
                            <GrDocumentZip />
                            <span>Download Bundle</span>
                          </span>
                        )
                      }
                    />
                  ) : null}
                </div>

                {(bundle?.is_featured || bundle?.is_monthSpecial || hasCreditDiscount) && (
                  <div className="download_chip">
                    {bundle?.is_featured && <Chip label="Featured" />}
                    {bundle?.is_monthSpecial && <Chip label="Special" />}
                    {hasCreditDiscount && (
                      <Chip label={`${getDiscountPercentage(userTier)}% Tier Discount`} />
                    )}
                  </div>
                )}
              </>
            )}
          </Col>

          <Col md={12} lg={12} xs={12} className="product-details">
            <p className="d-flex flex-column gap-2 mb-4 mt-4">
              <b className="text-pink">Info : </b>
              {categoryName && <span>Category : {categoryName}</span>}
              {bundle?.event_name && <span>Event : {bundle.event_name}</span>}
              <span>Assets included : {bundle?.count_of_assets || 0}</span>
              {minRequiredTier > 0 && (
                <span>Required plan : {tierNames[minRequiredTier] || "Subscription"}</span>
              )}
            </p>

            <div>
              <p className="d-flex flex-column gap-2 mb-4">
                <b className="text-pink">Enterprise Authorization : </b>
                <span>1. We do not accept AI-generated content into our visual library</span>
                <span>2. Includes our standard license.</span>
                <span>3. The files will be of much higher resolution than the preview images.</span>
                <span>4. All assets are delivered together as a single ZIP download.</span>
              </p>
            </div>
          </Col>
        </Row>

        {relatedLoading
          ? renderProductSlider("Related Bundles", [], true)
          : renderProductSlider("Related Bundles", relatedBundles, false, renderBundleCard)}

        {recommendationsLoading ? (
          renderProductSlider("Recommended Products", [], true)
        ) : (
          recommendationSections.map((section) => (
            <React.Fragment key={section.key}>
              {renderProductSlider(section.title, section.data)}
            </React.Fragment>
          ))
        )}

        <div className="pb-100" />
      </Container>
    </>
  );
};

export default Bulkproductdetails;
